import React, { useState, useEffect } from 'react';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { GlobalStateProvider } from './contexts/GlobalStateContext';
import { TradingDataProvider } from './contexts/TradingDataContext';

// Import all widget components
import PositionStatus from './components/PositionStatus';
import CryptoDashboard from './components/CryptoDashboard';
import BotTimer from './components/BotTimer';
import AIAnalysis from './components/AIAnalysis';
import TradingStats from './components/TradingStats';
import ProfitStatus from './components/ProfitStatus';
import BinanceBalance from './components/BinanceBalance';
import PriceChart from './components/PriceChart';
import BotControl from './components/BotControl';

import OrderBook from './components/OrderBook';
import AIChatbot from './components/AIChatbot';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Settings from './components/Settings';
import Strategies from './components/Strategies';
import Backtest from './components/Backtest';
import Reports from './components/Reports';
import Security from './components/Security';
import AIPredictionPerformance from './components/AIPredictionPerformance';
import RealTimeNews from './components/RealTimeNews';

const AppContent: React.FC = () => {
  const { theme } = useTheme();
  
  // 햄버거 메뉴를 확실히 닫힌 상태로 시작 - 로컬스토리지 무시하고 항상 false
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [highlightSection, setHighlightSection] = useState<string | undefined>(undefined);

  // 컴포넌트 마운트 시 사이드바를 확실히 닫힌 상태로 설정
  useEffect(() => {
    setIsSidebarOpen(false);
    try {
      localStorage.setItem('sidebarOpen', 'false');
    } catch (error) {
      console.log('로컬스토리지 설정 실패:', error);
    }
  }, []);

  // 화면 크기 변화 감지
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      
      setIsMobile(mobile);
      
      // 모바일에서는 사이드바를 자동으로 닫기
      if (mobile) {
        setIsSidebarOpen(false);
        try {
          localStorage.setItem('sidebarOpen', 'false');
        } catch (error) {
          console.log('로컬스토리지 설정 실패:', error);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    console.log('🍔 햄버거 버튼 클릭됨, 현재 상태:', isSidebarOpen);
    const newState = !isSidebarOpen;
    setIsSidebarOpen(newState);
    try {
      localStorage.setItem('sidebarOpen', newState.toString());
    } catch (error) {
      console.log('로컬스토리지 설정 실패:', error);
    }
    console.log('🍔 새로운 상태:', newState);
  };

  const resetLayout = () => {
    console.log('Layout reset');
    setCurrentView('dashboard');
  };

  const handleViewChange = (viewId: string) => {
    setCurrentView(viewId);
    setHighlightSection(undefined); // 기본적으로 하이라이트 제거
    // 모바일에서 뷰 변경 시 사이드바 자동 닫기
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  const handleViewChangeWithHighlight = (viewId: string, highlight?: string) => {
    setCurrentView(viewId);
    setHighlightSection(highlight);
    // 모바일에서 뷰 변경 시 사이드바 자동 닫기
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  const renderView = () => {
    // 반응형 그리드 클래스 정의
    const responsiveGrid = {
      dashboard: isMobile 
        ? 'grid grid-cols-1 gap-3' 
        : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4',
      trading: isMobile 
        ? 'grid grid-cols-1 gap-3' 
        : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4',
      positions: isMobile 
        ? 'grid grid-cols-1 gap-3' 
        : 'grid grid-cols-1 md:grid-cols-2 gap-4',
      analytics: isMobile 
        ? 'grid grid-cols-1 gap-3' 
        : 'grid grid-cols-1 md:grid-cols-2 gap-4',
      botControl: isMobile 
        ? 'grid grid-cols-1 gap-3' 
        : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4',
      aiChat: isMobile 
        ? 'grid grid-cols-1 gap-3' 
        : 'grid grid-cols-1 md:grid-cols-2 gap-4',

      single: isMobile ? 'grid grid-cols-1 gap-3' : 'grid grid-cols-1 gap-4'
    };

    switch (currentView) {
      case 'dashboard':
        return (
          <div className={responsiveGrid.dashboard}>
            {/* Top Row - 4 main widgets */}
            {/* 암호화폐 대시보드 */}
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg ${isMobile ? 'min-h-[200px]' : 'min-h-[280px] lg:min-h-[320px]'} col-span-1`}>
              <CryptoDashboard />
            </div>
            
            {/* 봇 타이머 */}
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg ${isMobile ? 'min-h-[200px]' : 'min-h-[280px] lg:min-h-[320px]'} col-span-1`}>
              <BotTimer />
            </div>
            
            {/* 봇 제어 */}
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg ${isMobile ? 'min-h-[200px]' : 'min-h-[280px] lg:min-h-[320px]'} col-span-1`}>
              <BotControl />
            </div>
            
            {/* 바이낸스 잔고 */}
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg ${isMobile ? 'min-h-[200px]' : 'min-h-[280px] lg:min-h-[320px]'} col-span-1`}>
              <BinanceBalance />
            </div>
            
            {/* Second Row - Combined Row with AI Analysis (left 2 columns) and Right sidebar with Real-time News + AI Prediction Performance */}
            {/* AI 투자분석 보고서 (left 2 columns) */}
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg ${isMobile ? 'min-h-[300px] col-span-1' : 'min-h-[350px] lg:min-h-[380px] col-span-2 lg:col-span-2'}`}>
              <AIAnalysis onNavigate={handleViewChange} onNavigateWithHighlight={handleViewChangeWithHighlight} />
            </div>
            
            {/* Right sidebar with Real-time News + AI Prediction Performance (right 2 columns) */}
            <div className={`${isMobile ? 'col-span-1 space-y-2' : 'col-span-2 lg:col-span-2 flex flex-col space-y-3'}`}>
              {/* 실시간 뉴스 (increased height more) */}
              <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg ${isMobile ? 'h-[400px]' : 'h-[500px] lg:h-[700px]'}`}>
                <RealTimeNews />
              </div>
            
              {/* AI 예측 성과 분석 (reduced height more) */}
              <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg ${isMobile ? 'min-h-[200px]' : 'min-h-[180px] lg:min-h-[220px] flex-grow'}`}>
                <AIPredictionPerformance />
              </div>
            </div>
            
            {/* Third Row - Price Chart and AI Assistant */}
            {/* BTC/USDT 차트 (left 2 columns) */}
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg ${isMobile ? 'min-h-[500px] col-span-1' : 'min-h-[600px] lg:min-h-[760px] col-span-2 lg:col-span-2'}`}>
              <PriceChart />
            </div>
            
            {/* AI 어시스턴트 (right 2 columns - reduced height) */}
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg ${isMobile ? 'min-h-[500px] col-span-1' : 'min-h-[600px] lg:min-h-[760px] col-span-2 lg:col-span-2'}`}>
              <AIChatbot />
            </div>

            {/* Fourth Row - Additional 4 widgets */}
            {/* 호가창 */}
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg ${isMobile ? 'min-h-[200px]' : 'min-h-[280px] lg:min-h-[320px]'} col-span-1`}>
              <OrderBook />
            </div>
            
            {/* 거래 통계 */}
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg ${isMobile ? 'min-h-[200px]' : 'min-h-[280px] lg:min-h-[320px]'} col-span-1`}>
              <TradingStats />
            </div>
            
            {/* 현재 포지션 */}
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg ${isMobile ? 'min-h-[200px]' : 'min-h-[280px] lg:min-h-[320px]'} col-span-1`}>
              <PositionStatus />
            </div>
            
            {/* 수익 상태 */}
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg ${isMobile ? 'min-h-[200px]' : 'min-h-[280px] lg:min-h-[320px]'} col-span-1`}>
              <ProfitStatus />
            </div>
            

          </div>
        );

      case 'trading':
        return (
          <div className={responsiveGrid.trading}>
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[400px] ${isMobile ? 'col-span-1' : 'md:col-span-2 lg:col-span-2'}`}>
              <PriceChart />
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[400px]">
              <PositionStatus />
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[400px]">
              <OrderBook />
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[400px]">
              <CryptoDashboard />
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[400px]">
              <TradingStats />
            </div>
          </div>
        );

      case 'positions':
        return (
          <div className={responsiveGrid.positions}>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[500px]">
              <PositionStatus />
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[500px]">
              <ProfitStatus />
            </div>
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[400px] ${isMobile ? 'col-span-1' : 'md:col-span-2'}`}>
              <TradingStats />
            </div>
          </div>
        );

      case 'analytics':
        return (
          <div className={responsiveGrid.analytics}>
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[500px] ${isMobile ? 'col-span-1' : 'md:col-span-2'}`}>
              <PriceChart />
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[400px]">
              <AIAnalysis onNavigate={handleViewChange} onNavigateWithHighlight={handleViewChangeWithHighlight} />
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[400px]">
              <TradingStats />
            </div>
          </div>
        );

      case 'bot-control':
        return (
          <div className={responsiveGrid.botControl}>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[400px]">
              <BotControl />
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[400px]">
              <BotTimer />
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[400px]">
              <AIAnalysis onNavigate={handleViewChange} onNavigateWithHighlight={handleViewChangeWithHighlight} />
            </div>
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[300px] ${isMobile ? 'col-span-1' : 'md:col-span-2 lg:col-span-3'}`}>
              <TradingStats />
            </div>
          </div>
        );

      case 'ai-chat':
        return (
          <div className={responsiveGrid.aiChat}>
            <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg min-h-[600px] ${isMobile ? 'col-span-1' : 'md:col-span-2'}`}>
              <AIChatbot />
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[400px]">
              <AIAnalysis onNavigate={handleViewChange} onNavigateWithHighlight={handleViewChangeWithHighlight} />
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[400px]">
              <CryptoDashboard />
            </div>
          </div>
        );



      case 'settings':
        return (
          <div className={responsiveGrid.single}>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[600px]">
              <Settings highlightSection={highlightSection} />
            </div>
          </div>
        );

      case 'strategies':
        return (
          <div className={responsiveGrid.single}>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[600px]">
              <Strategies />
            </div>
          </div>
        );

      case 'backtest':
        return (
          <div className={responsiveGrid.single}>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[600px]">
              <Backtest />
            </div>
          </div>
        );

      case 'reports':
        return (
          <div className={responsiveGrid.single}>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[600px]">
              <Reports />
            </div>
          </div>
        );

      case 'security':
        return (
          <div className={responsiveGrid.single}>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg min-h-[600px]">
              <Security />
            </div>
          </div>
        );

      default:
        // 기본: 대시보드로 돌아가기
        return (
          <div className="text-center py-20">
            <h2 className="text-2xl text-gray-900 dark:text-white mb-4">페이지를 찾을 수 없습니다</h2>
            <button 
              onClick={() => setCurrentView('dashboard')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              대시보드로 돌아가기
            </button>
          </div>
        );
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-200 ${
      theme === 'dark' ? 'dark bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
    }`}>
      <Header 
        onToggleSidebar={toggleSidebar} 
        onResetLayout={resetLayout}
        onViewChange={handleViewChange}
        isMenuOpen={isSidebarOpen}
      />
      
      <div className="flex relative">
        {/* 모바일에서 사이드바 열림 시 백드롭 */}
        {isMobile && isSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-30 pt-16"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
        
        <Sidebar 
          isOpen={isSidebarOpen} 
          currentView={currentView}
          onViewChange={handleViewChange}
          onClose={() => setIsSidebarOpen(false)}
          isMobile={isMobile}
        />
        
        <main className={`flex-1 transition-all duration-300 pt-16 ${
          !isMobile && isSidebarOpen 
            ? 'ml-64' // 데스크톱에서 사이드바 열림
            : 'ml-0'  // 모바일이거나 사이드바 닫힘
        }`}>
          <div className={`${isMobile ? 'p-2' : 'p-2 sm:p-4'}`}>
            {/* 페이지 제목 */}
            <div className="mb-3">
              <h1 className={`font-bold text-gray-900 dark:text-white ${isMobile ? 'text-lg' : 'text-lg sm:text-2xl'}`}>
                {currentView === 'dashboard' && '📊 대시보드'}
                {currentView === 'trading' && '💰 거래'}
                {currentView === 'positions' && '📈 포지션 관리'}
                {currentView === 'analytics' && '📊 분석'}
                {currentView === 'bot-control' && '🤖 봇 제어'}
                {currentView === 'ai-chat' && '🤖 AI 어시스턴트'}
                {currentView === 'alerts' && '🔔 알림'}
                {currentView === 'settings' && '⚙️ 설정'}
                {currentView === 'strategies' && '🎯 전략'}
                {currentView === 'backtest' && '⏰ 백테스트'}
                {currentView === 'reports' && '📄 보고서'}
                {currentView === 'security' && '🔒 보안'}
              </h1>
            </div>
            
            {/* 현재 뷰 렌더링 */}
            {renderView()}
          </div>
        </main>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <ThemeProvider>
        <GlobalStateProvider>
          <TradingDataProvider>
            <AppContent />
          </TradingDataProvider>
        </GlobalStateProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
};

export default App;
